import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { LookupModel } from '../models/common/lookup.model';

import { MemberAccess } from '../models/common/member-access.model';
import { MemberPermissionSale } from '../models/sales/membe-permission-sale.model';
import { SaleConsignorMailModel } from '../models/sales/sale-consignor-mail.model';
import { ConsignorContact, SaleConsignorModel } from '../models/sales/sale-consignor.model';
import { SaleHorseNoteModel } from '../models/sales/sale-horse-note.model';
import { SaleHorseSetting } from '../models/sales/sale-horse-settings.model';
import { SaleHorseStudyFiles } from '../models/sales/sale-horse-study-files.model';
import { GetDisclosureClientName } from '../models/sales/DisclosedClient.model';

import { SaleHorse, SaleHorseFlat, SaleHorseStatus } from '../models/sales/sale-horse.model';
import {
  SaleMemberRequestModel,
  SaleMemberRequests,
} from '../models/sales/sale-member-request.model';
import { SaleSettingModel } from '../models/sales/sale-settings.model';
import { SaleVetModel } from '../models/sales/sale-vet.model';
import { SaleGranted } from '../models/sales/sales-granted.model';
import { Sale, SaleDeadlines, SaleSubmissions } from '../models/sales/sales.model';
import { SpinnerService } from '../shared-new/services/spinner.service';
import { ConsignorProfile } from '../models/repository/consignor-profile';
import { SaleHorseRepoContactRequestDto, SaleHorseRepoContactDto, SaleHorseRepoContactResponseDto } from '../models/sales/sale-contacts.model';
import { SaleMediaConversionLog } from '../models/sales/sale-media-conversion-log.model';

@Injectable({
  providedIn: 'root',
})
export class SalesService {
  public baseUrl = environment.api2BaseUrl;
  public authBaseUrl = environment.apiAuthUrl;
  public onError: BehaviorSubject<string> = new BehaviorSubject('');
  public serviceParameter = {
    'Content-Type': 'application/json; charset=utf-8',
    Accept: 'application/json',
    Authorization: '',
  };

  public constructor(private readonly http: HttpClient,private spinnerService: SpinnerService) {}

  public getSale(saleId: number): Observable<Sale> {
    this.spinnerService.addRequest('getSale');
    return this.http
      .get<Sale>(`${this.baseUrl}/api/Sales/${saleId}`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getSale'))
      );
  }

  public getSaleDeadLine(saleId: number): Observable<SaleDeadlines> {
    this.spinnerService.addRequest('getSaleDeadLine');
    return this.http
      .get<SaleDeadlines>(`${this.baseUrl}/api/Sales/${saleId}/deadlines`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getSaleDeadLine'))
      );
  }

  getConsignorByUID(consignorUID: string): Observable<ConsignorProfile> {
    this.spinnerService.addRequest('getConsignorByUID');
    return this.http
      .get<ConsignorProfile>(`${this.baseUrl}/api/Sales/consignorProfile/${consignorUID}`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getConsignorByUID'))
      );
  }

  getConsignorContacts(consignorId: number): Observable<ConsignorContact[]> {
    this.spinnerService.addRequest('getConsignorContact');
    return this.http
      .get<ConsignorContact[]>(`${this.baseUrl}/api/Consignors/${consignorId}/ConsignorContacts`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getConsignorContact'))
      );
  }

  saveConsignorContacts(consignorId: number, contacts: ConsignorContact[]): Observable<ConsignorContact[]> {
    this.spinnerService.addRequest('saveConsignorContact');    
    return this.http
      .post<ConsignorContact[]>(`${this.authBaseUrl}/repository/${consignorId}/ConsignorContacts`, contacts,
        {
          withCredentials: false,
          observe: 'response',
        })
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('saveConsignorContact'))
      );
  }

  public getSaleByUID(saleUID: string): Observable<Sale> {
    this.spinnerService.addRequest('getSaleByUID');
    return this.http
      .get<Sale>(`${this.baseUrl}/api/Sales/uid/${saleUID}`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getSaleByUID'))
      );
  }
  
  public shareHorseStudy(data: any): Observable<boolean> {
    this.spinnerService.addRequest('posthareToHorseEmails');
    return this.http
      .post<boolean>(`${this.baseUrl}/api/sales/shareToEmails`, data)
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('posthareToHorseEmails'))
      );
  }

  public getSaleSettings(saleId: number): Observable<SaleSettingModel> {
    this.spinnerService.addRequest('getSaleSettings');
    return this.http
      .get<SaleSettingModel>(`${this.baseUrl}/api/Sales/${saleId}/settings`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getSaleSettings'))
      );
  }
  /**
   * Client names this vet's clinic subscription has declared before. This is the ENTIRE
   * declare-client picker list. Takes no parameters — the member is resolved from the
   * caller's token server-side, so it can never return another clinic's names.
   *
   * The picker used to list TwAuthApi /repository/{saleId}/saleViewers, which returns every
   * clinic's declared clients for the sale. Do not reintroduce it.
   *
   * Failures resolve to an empty list rather than propagating: the vet must still be able
   * to type and declare a client if this call fails.
   */
  public getMyClientNames(): Observable<string[]> {
    return this.http
      .get<string[]>(`${this.baseUrl}/api/Activity/myClientNames`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => res.body ?? []),
        catchError(() => of([] as string[]))
      );
  }

  /**
   * The declare-client picker's list for one sale: { enabled, names }.
   *
   * enabled is false when ThoroughAdmin has switched this clinic's client name list off for
   * the sale's sales company (dbo.ClientNameListOff); names is then empty and the picker
   * shows no suggestions. The vet still types a name.
   *
   * An API without the route (legacy prod before the switch shipped) answers 404/405; that
   * falls back to myClientNames, treated as enabled. Any other failure resolves to an empty,
   * enabled list so the vet can always type and declare a client.
   */
  public getClientNameList(saleId: number | null | undefined): Observable<{ enabled: boolean; names: string[] }> {
    const id = Number(saleId);
    const query = Number.isInteger(id) && id > 0 ? `?saleId=${id}` : '';
    return this.http
      .get<{ enabled?: unknown; names?: unknown }>(`${this.baseUrl}/api/Activity/clientNameList${query}`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          const body = res.body ?? {};
          const names = Array.isArray(body.names) ? body.names.filter((n): n is string => typeof n === 'string') : [];
          return { enabled: body.enabled !== false, names };
        }),
        catchError((error) =>
          error?.status === 404 || error?.status === 405
            ? this.getMyClientNames().pipe(map((names) => ({ enabled: true, names })))
            : of({ enabled: true, names: [] as string[] })
        )
      );
  }

  public getSaleHorseSettings(
    saleId: number,
    horseId: number
  ): Observable<SaleHorseSetting> {
    this.spinnerService.addRequest('getSaleHorseSettings');
    return this.http
      .get<SaleHorseSetting>(
        `${this.baseUrl}/api/Sales/${saleId}/${horseId}/settings`,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getSaleHorseSettings'))
      );
  }
  public getSaleViewerSettings(saleId: number): Observable<SaleSettingModel> {
    this.spinnerService.addRequest('getSaleViewerSettings');
    return this.http
      .get<SaleSettingModel>(
        `${this.baseUrl}/api/Sales/${saleId}/settings/viewer`,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getSaleViewerSettings'))
      );
  }
  public updateSaleSettings(
    saleSetting: SaleSettingModel
  ): Observable<SaleSettingModel> {
    this.spinnerService.addRequest('updateSaleSettings');
    return this.http
      .post<SaleSettingModel>(
        `${this.baseUrl}/api/sales/updatesettings`,
        saleSetting,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('updateSaleSettings'))
      );
  }

  public getSales(): Observable<Sale[]> {
    this.spinnerService.addRequest('getSales');
    return this.http
      .get<Sale[]>(`${this.baseUrl}/api/Sales`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getSales'))
      );
  }
  public joinedSaleVet(saleMemberAccessId: number): Observable<SaleGranted> {
    this.spinnerService.addRequest('joinedSaleVet');
   
    return this.http
      .get<SaleGranted>(
        `${this.baseUrl}/api/Sales/${saleMemberAccessId}/JoinedSaleVet`,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('joinedSaleVet'))
      );
  }

  public getSaleLookup(): Observable<LookupModel[]> {
    this.spinnerService.addRequest('getSaleLookup');
    return this.http
      .get<LookupModel[]>(`${this.baseUrl}/api/sales/salelookup`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getSaleLookup'))
      );
  }

  public getSaleHorses(saleId: number): Observable<SaleHorse[]> {
    this.spinnerService.addRequest('getSaleHorses');
    return this.http
      .get<SaleHorse[]>(`${this.baseUrl}/api/Sales/${saleId}/Horses`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getSaleHorses'))
      );
  }

  public getSaleHorse(saleId: number, horseId: number): Observable<SaleHorse> {
    this.spinnerService.addRequest('getSaleHorse');
    return this.http
      .get<SaleHorse>(`${this.baseUrl}/api/Sales/${saleId}/${horseId}/Horse`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getSaleHorse'))
      );
  }
  public getSaleHorseResult(saleId: number, horseId: number): Observable<SaleHorse> {
    this.spinnerService.addRequest('getSaleHorse');
    return this.http
      .get<SaleHorse>(`${this.authBaseUrl}/repository/${saleId}/${horseId}/salehorse/Status`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getSaleHorse'))
      );
  }

  public getSaleHorsesFlat(saleId: number): Observable<SaleHorseFlat[]> {
    this.spinnerService.addRequest('getSaleHorsesFlat');
    return this.http
      .get<SaleHorseFlat[]>(`${this.baseUrl}/api/Sales/${saleId}/Horses/Flat`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getSaleHorsesFlat'))
      );
  }

  public getSaleConsignors(saleId: number): Observable<SaleConsignorModel[]> {
    this.spinnerService.addRequest('getSaleConsignors');
    return this.http
      .get<SaleConsignorModel[]>(
        `${this.baseUrl}/api/Sales/${saleId}/Consignors`,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getSaleConsignors'))
      );
  }

  public getSaleVets(saleId: number): Observable<SaleVetModel[]> {
    this.spinnerService.addRequest('getSaleVets');
    return this.http
      .get<SaleVetModel[]>(`${this.baseUrl}/api/Sales/${saleId}/Vets`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getSaleVets'))
      );
  }

  public getSaleTermsAndConditions(): Observable<{}> {
    this.spinnerService.addRequest('getSaleTermsAndConditions');
    return this.http.get(`${this.baseUrl}/api/SaleTermsAndConditions`, {
      withCredentials: false,
      observe: 'response',
    }).pipe(
      map((res) => {
        return res.body;
      }),
      catchError((error) => {
        this.onError.next(error.message);
        return throwError(error);
      }),
      finalize(() => this.spinnerService.popRequest('getSaleTermsAndConditions'))
    );
  }

  public acceptSaleTermsAndConditions(saleId: number): Observable<boolean> {
    this.spinnerService.addRequest('acceptSaleTermsAndConditions');
    return this.http
      .get<boolean>(`${this.baseUrl}/api/Sales/${saleId}/TermsAccept`)
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('acceptSaleTermsAndConditions'))
      );
  }
  public deleteSaleHorseNote(id: number): Observable<boolean> {
    this.spinnerService.addRequest('deleteSaleHorseNote');
    return this.http
      .delete<boolean>(`${this.baseUrl}/api/SaleHorseNotes/${id}/Notes`)
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('deleteSaleHorseNote'))
      );
  }
  public getSaleHorseNotes(
    saleId: number,
    horseId: number
  ): Observable<SaleHorseNoteModel[]> {
    this.spinnerService.addRequest('getSaleHorseNotes');
    return this.http
      .get<SaleHorseNoteModel[]>(
        `${this.baseUrl}/api/SaleHorseNotes/${saleId}/${horseId}/Notes`,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getSaleHorseNotes'))
      );
  }
  public getSaleHorseNote(id: number): Observable<SaleHorseNoteModel> {
    this.spinnerService.addRequest('getSaleHorseNote');
    return this.http
      .get<SaleHorseNoteModel>(
        `${this.baseUrl}/api/SaleHorseNotes/${id}/note`,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getSaleHorseNote'))
      );
  }
  public postSaleHorseNotes(saleTerms: any): Observable<SaleHorseNoteModel> {
    this.spinnerService.addRequest('postSaleHorseNotes');
    return this.http
      .post<SaleHorseNoteModel>(`${this.baseUrl}/api/SaleHorseNotes`, saleTerms)
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('postSaleHorseNotes'))
      );
  }

  public getSaleTemplate(saleId: number): Observable<SaleHorse[]> {
    this.spinnerService.addRequest('getSaleTemplate');
    return this.http
      .get<SaleHorse[]>(
        `${this.baseUrl}/api/Settings/Sale/${saleId}/SaleTemplate`,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getSaleTemplate'))
      );
  }

  public sendSaleMemberRequestAccess(
    model: SaleMemberRequestModel
  ): Observable<Boolean> {
    this.spinnerService.addRequest('sendSaleMemberRequestAccess');
    return this.http
      .post<Boolean>(`${this.baseUrl}/api/Sales/RequestAccessToSale`, model, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('sendSaleMemberRequestAccess'))
      );
  }
  public acceptSaleMemberRequestAccess(
    data: any
  ): Observable<SaleMemberRequests[]> {
    this.spinnerService.addRequest('acceptSaleMemberRequestAccess');
    return this.http
      .post<SaleMemberRequests[]>(
        `${this.baseUrl}/api/Sales/AcceptSaleMemberRequest`,
        data,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('acceptSaleMemberRequestAccess'))
      );
  }
  public getSaleMemberRequests(
    saleId: number,
    saleMemberStatusId: number
  ): Observable<SaleMemberRequests[]> {
    this.spinnerService.addRequest('getSaleMemberRequests');
    return this.http
      .get<SaleMemberRequests[]>(
        `${this.baseUrl}/api/Sales/${saleId}/${saleMemberStatusId}/SaleMemberRequests`,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getSaleMemberRequests'))
      );
  }

  public getSaleSubmissions(saleId: number): Observable<SaleSubmissions[]> {
    this.spinnerService.addRequest('getSaleSubmissions');
    return this.http
      .get<SaleSubmissions[]>(
        `${this.baseUrl}/api/Studies/${saleId}/submissions`,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getSaleSubmissions'))
      );
  }

  //public getSaleSubmission(submissionId: number): Observable<SaleSubmissions> {
  //  this.spinnerService.addRequest('getSaleSubmission');
  //  return this.http
  //    .get<SaleSubmissions>(
  //      `${this.baseUrl}/api/Studies/${submissionId}/submission`,
  //      {
  //        withCredentials: false,
  //        observe: 'response',
  //      }
  //    )
  //    .pipe(
  //      map((res) => {
  //        return res.body;
  //      }),
  //      catchError((error) => {
  //        this.onError.next(error.message);
  //        return throwError(error);
  //      }),
  //      finalize(() => this.spinnerService.popRequest('getSaleSubmission'))
  //    );
  //}

  public postSaleSubmissions(requestData: {
    id: number;
    statusId: number;
    comments: string;
  }): Observable<SaleSubmissions[]> {
    this.spinnerService.addRequest('postSaleSubmissions');
    return this.http
      .post<SaleSubmissions[]>(
        `${this.baseUrl}/api/Studies/submissions`,
        requestData,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('postSaleSubmissions'))
      );
  }

  public updateSaleHorseStatus(saleHorse: SaleHorseStatus): Observable<boolean> {
    this.spinnerService.addRequest('updateSaleHorseStatus');
    return this.http
      .put<boolean>(
        `${this.authBaseUrl}/repository/salehorse/status`,
        saleHorse,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('updateSaleHorseStatus'))
      );
  }

  public updateSaleMemberAccess(model: MemberPermissionSale[]): Observable<boolean> {
    this.spinnerService.addRequest('updateSaleMemberAccess');
    return this.http
      .put<boolean>(
        `${this.baseUrl}/api/Sales/SaleMemberAccess`,
        model,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('updateSaleMemberAccess'))
      );
  }

  public updateVetSettings(model: any): Observable<boolean> {
    this.spinnerService.addRequest('updateSaleMemberAccess');
    return this.http
      .post<boolean>(
        `${this.baseUrl}/api/Sales/UpdateVetSettings`,
        model,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('updateSaleMemberAccess'))
      );
  }
   
  public getSaleMemberAccess(memberId: number): Observable<MemberPermissionSale[]> {
    this.spinnerService.addRequest('geSaleMemberAccess');
    return this.http
      .get<MemberPermissionSale[]>(`${this.baseUrl}/api/Sales/${memberId}/SaleMemberAccess`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('geSaleMemberAccess'))
      );
  }

  public getHorsesByLotNumber(lotNumber: string): Observable<LookupModel[]> {
    this.spinnerService.addRequest('getHorsesByLotNumber');
    const model: LookupModel = {
      id: 0,
      name: lotNumber,
      altName: '',
      selected: false,
    };
    return this.http
      .post<LookupModel[]>(`${this.baseUrl}/api/Sales/Search/Horses`, model, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getHorsesByLotNumber'))
      );
  }

  public getSaleHorseStudyFiles(
    saleId: number,
    horseId: number
  ): Observable<SaleHorseStudyFiles[]> {
    this.spinnerService.addRequest('getSaleHorseStudyFiles');
    return this.http
      .get<SaleHorseStudyFiles[]>(
        `${this.baseUrl}/api/StudyFiles/${saleId}/${horseId}`,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getSaleHorseStudyFiles'))
      );
  }

  /*
  public postEmailToConsignors(
    saleConsignorMailModel: SaleConsignorMailModel
  ): Observable<SaleConsignorMailModel[]> {
    this.spinnerService.addRequest('postEmailToConsignors');
    return this.http
      .post<SaleConsignorMailModel[]>(
        `${this.baseUrl}/api/mail/consignors`,
        saleConsignorMailModel,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('postEmailToConsignors'))
      );
  }
*/
  public getMemberAccessForSale(saleId: number): Observable<MemberAccess> {
    this.spinnerService.addRequest('getMemberAccessForSale');
    return this.http
      .get<MemberAccess>(`${this.baseUrl}/api/member/memberAccess?saleId=${saleId}`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getMemberAccessForSale'))
      );
  }

  public saveSaleHorseRepoContact(saleId: number, model: SaleHorseRepoContactRequestDto): Observable<boolean> {
    this.spinnerService.addRequest('saveSaleHorseRepoContact');
    return this.http
      .post<boolean>(`${this.baseUrl}/api/Sales/${saleId}/SaveSaleHorseRepoContact`, model, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => res.body as boolean),
        catchError((error) => {
          console.error(error);
          return throwError(() => error);
        }),
        finalize(() => this.spinnerService.popRequest('saveSaleHorseRepoContact'))
      );
  }

  public getRepoContactBySaleId(saleId: number): Observable<SaleHorseRepoContactDto[]> {
    this.spinnerService.addRequest('getRepoContactBySaleId');
    return this.http
      .get<SaleHorseRepoContactDto[]>(`${this.baseUrl}/api/Sales/${saleId}/GetRepoContactBySaleId`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => res.body || []),
        catchError((error) => {
          console.error(error);
          return throwError(() => error);
        }),
        finalize(() => this.spinnerService.popRequest('getRepoContactBySaleId'))
      );
  }

  public getRepoContactHorsesBySaleId(saleId: number, contactId: number): Observable<SaleHorseRepoContactResponseDto[]> {
    this.spinnerService.addRequest('getRepoContactHorsesBySaleId');
    return this.http
      .get<SaleHorseRepoContactResponseDto[]>(`${this.baseUrl}/api/Sales/${saleId}/${contactId}/GetRepoContactHorsesBySaleId`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => res.body || []),
        catchError((error) => {
          return throwError(() => error);
        }),
        finalize(() => this.spinnerService.popRequest('getRepoContactHorsesBySaleId'))
      );
  }

  public checkSaleRelatedDataExists(saleId: number): Observable<boolean> {
    this.spinnerService.addRequest('checkSaleRelatedDataExists');

    return this.http
      .get<boolean>(
        `${this.baseUrl}/api/Sales/${saleId}/hasRelatedData`,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => res.body ?? false),
        catchError((error) => {
          return throwError(() => error);
        }),
        finalize(() => this.spinnerService.popRequest('checkSaleRelatedDataExists'))
      );
  }


  public deleteSaleHorseRepoContact(saleId: number, saleHorseRepoContactId: number): Observable<boolean> {
    this.spinnerService.addRequest('deleteSaleHorseRepoContact');
    return this.http
      .delete<boolean>(`${this.baseUrl}/api/Sales/${saleId}/${saleHorseRepoContactId}/DeleteSaleHorseRepoContact`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => res.body as boolean),
        catchError((error) => {
          return throwError(() => error);
        }),
        finalize(() => this.spinnerService.popRequest('deleteSaleHorseRepoContact'))
      );
  }

  public deleteSaleHorseRepoContacts(saleId: number, contactId: number): Observable<boolean> {
    this.spinnerService.addRequest('deleteSaleHorseRepoContacts');
    return this.http
      .delete<boolean>(`${this.baseUrl}/api/Sales/${saleId}/${contactId}/DeleteSaleHorseRepoContacts`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => res.body as boolean),
        catchError((error) => {
          return throwError(() => error);
        }),
        finalize(() => this.spinnerService.popRequest('deleteSaleHorseRepoContacts'))
      );
  }

  public getSaleMediaConversionLogs(saleId: number): Observable<SaleMediaConversionLog[]> {
    this.spinnerService.addRequest('getSaleMediaConversionLogs');
    return this.http
      .get<SaleMediaConversionLog[]>(
        `${this.baseUrl}/api/Sales/${saleId}/media-conversion-logs`,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => res.body ?? []),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(() => error);
        }),
        finalize(() => this.spinnerService.popRequest('getSaleMediaConversionLogs'))
      );
  }
}

